import { useWindowDimensions } from 'react-native';
import { IconCaretDownFilled, IconCirclePlus, IconPlus } from '@tabler/icons-react-native';
import { Box, useTheme } from 'app/shopify/theme';
import { INTERACTION } from 'app/shopify/generated/interaction';
import { ActionPressable, type ActionProps } from '../shared/ActionPressable';
import { ActionLabel, ActionSpinner } from '../shared/ActionContent';

export interface PublishButtonProps extends ActionProps {
  label: string;
  size?: 'small' | 'medium' | 'large';
  /** Hide the menu indicator when the action navigates directly. */
  showChevron?: boolean;
  /** Defaults to the viewport breakpoint; small is the bottom-tab icon treatment. */
  breakpoint?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
}

export function PublishButton({
  label,
  size = 'large',
  showChevron = true,
  breakpoint,
  fullWidth,
  accessibilityLabel = label,
  ...props
}: PublishButtonProps) {
  const theme = useTheme();
  const { width } = useWindowDimensions();
  const compact = breakpoint ? breakpoint === 'small' : width < theme.breakpoints.medium;
  const LeadingIcon = compact ? IconPlus : IconCirclePlus;
  const iconSize = Number(
    theme.tokens.sizing[
      compact
        ? 'sizing.icon.lg'
        : size === 'large'
          ? 'sizing.icon.default'
          : size === 'small'
            ? 'sizing.icon.s'
            : 'sizing.icon.sm'
    ],
  );
  const color =
    theme.colors[
      compact ? 'white-label.publish-button.icon.mobile-bottom-tab-bar' : 'white-label.publish-button.icon.header-bar'
    ];
  const dimension = Number(
    theme.tokens.sizing[
      compact ? 'sizing.component.button.height.large-icn-button' : (`sizing.component.button.height.${size}` as const)
    ],
  );
  return (
    <ActionPressable
      {...props}
      accessibilityLabel={accessibilityLabel}
      fullWidth={!compact && fullWidth}
      width={compact ? dimension : undefined}
      minHeight={dimension}
      radius={theme.borderRadii[compact ? 'radius.component.publish-button.button-radius' : 'radius.full']}
      paddingHorizontal={compact ? 0 : theme.spacing['spacing.general.x6']}
      paddingVertical={compact ? 0 : theme.spacing['spacing.general.x1']}
      appearance={({ pressed, hovered, disabled }) => ({
        backgroundColor:
          theme.colors[
            compact ? 'white-label.publish-button.bg.mobile-bottom-tab-bar' : 'white-label.publish-button.bg.header-bar'
          ],
        opacity: disabled ? INTERACTION.opacity['040'] : 1,
        overlayColor:
          pressed || hovered
            ? theme.colors[pressed ? 'component.button.bg.pressed' : 'component.button.bg.hover']
            : undefined,
      })}
    >
      {({ loading }) => (
        <>
          <Box
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
            maxWidth="100%"
            style={{ opacity: loading ? 0 : 1 }}
            aria-hidden
          >
            <LeadingIcon size={iconSize} color={color} accessible={false} />
            {!compact ? (
              <>
                <Box flexShrink={1}>
                  <ActionLabel
                    label={label}
                    color={theme.colors['white-label.publish-button.label.primary']}
                    small={size !== 'large'}
                  />
                </Box>
                {showChevron ? <IconCaretDownFilled size={iconSize} color={color} accessible={false} /> : null}
              </>
            ) : null}
          </Box>
          {loading ? (
            <Box position="absolute" top={0} bottom={0} left={0} right={0} alignItems="center" justifyContent="center">
              <ActionSpinner color={color} size={!compact && size !== 'large' ? 'small' : 'large'} />
            </Box>
          ) : null}
        </>
      )}
    </ActionPressable>
  );
}
